import { useEffect, useState } from 'react';

/**
 * Captures the browser's `beforeinstallprompt` event at app startup (module
 * import) so the deferred prompt is still available later when the user opens
 * the profile menu to install the app. Import this module early (in index.js).
 */
let deferredPrompt = null;
let installed = false;
const listeners = new Set();

const notify = () => listeners.forEach((fn) => fn());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredPrompt = event;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    installed = true;
    deferredPrompt = null;
    notify();
  });
}

export function useInstallPrompt() {
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    const update = () => forceUpdate((n) => n + 1);
    listeners.add(update);
    return () => {
      listeners.delete(update);
    };
  }, []);

  const isStandalone =
    typeof window !== 'undefined' &&
    (window.matchMedia?.('(display-mode: standalone)').matches ||
      window.navigator.standalone === true);

  const isIOS =
    typeof window !== 'undefined' &&
    /iphone|ipad|ipod/i.test(window.navigator.userAgent || '');

  const promptInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    deferredPrompt = null;
    notify();
  };

  return {
    canInstall: !!deferredPrompt && !installed && !isStandalone,
    // Whether to surface an install option at all. On iOS the native
    // `beforeinstallprompt` never fires, so we still show the option and
    // display manual "Add to Home Screen" instructions instead.
    showInstall: !installed && !isStandalone,
    isIOS,
    promptInstall
  };
}

export default useInstallPrompt;
